/**
 * Candidate-Type-Specific Altitude Questions
 * 
 * Provides contextually appropriate questions for determining altitude (1-5)
 * based on the candidate_type (Problem, Opportunity, Solution, Risk, Investment, Commitment).
 * 
 * Altitude measures structural scope and complexity — NOT value or urgency.
 * Each question set maps to three structural dimensions:
 * 
 * - Reversibility (stored as timeHorizon): How locked in are we if we're wrong?
 *   1 = easily reversed/low-stakes  →  5 = near-irreversible/binding commitment
 * 
 * - Coordination Complexity (stored as workSize): How many groups must align?
 *   1 = one person/team  →  5 = org-wide or multi-org coordination
 * 
 * - Scope / Blast Radius (stored as impactSurface): How broadly does this reach?
 *   1 = isolated, one component  →  5 = company-wide or market-level
 * 
 * The framing differs so that questions make sense in context for each item type.
 * Value, urgency, and due dates are tracked separately — not encoded in altitude.
 * 
 * Usage:
 * 1. Shaping: Questions help assigners understand the structural scope
 * 2. Leveling: Questions help ensure items are at appropriate altitude for comparison
 */


import type { AltitudeScopingInputs, TimeHorizon, WorkSize, ImpactSurface } from '../types/prioritization/leveling';
import type { AltitudeLevel } from '../types/prioritization/itemLifecycle';
import type { ItemLanguageType } from '../types/prioritization/lifecycle';
import { listCategoryQuestions } from './itemCategoriesService';
import {
  IMPACT_SURFACE_BASE_OPTIONS,
  TIME_HORIZON_BASE_OPTIONS,
  WORK_SIZE_BASE_OPTIONS,
  type AltitudeQuestion,
  type AltitudeQuestionOption,
  type AltitudeQuestionResponse,
  type AltitudeQuestionSet,
} from '../../supabase/functions/_shared/altitudeQuestionsCore.ts';

// The question data and the arithmetic are the shared core (one copy with the
// server classify runner): supabase/functions/_shared/altitudeQuestionsCore.ts.
export {
  IMPACT_SURFACE_BASE_OPTIONS,
  TIME_HORIZON_BASE_OPTIONS,
  WORK_SIZE_BASE_OPTIONS,
  computeAltitudeFromResponses,
  getAllAltitudeQuestionSets,
  getAltitudeQuestionSet,
  type AltitudeQuestion,
  type AltitudeQuestionOption,
  type AltitudeQuestionResponse,
  type AltitudeQuestionSet,
} from '../../supabase/functions/_shared/altitudeQuestionsCore.ts';

// ============================================================================
// Question Set Assembly
// ============================================================================

/**
 * Build an AltitudeQuestionSet from DB rows (for custom categories or overridden builtins).
 * Returns null if no questions are found for this category.
 */
export async function getAltitudeQuestionSetFromDB(
  categorySlug: string,
  categoryId: string,
  categoryLabel: string,
  categoryDescription: string
): Promise<AltitudeQuestionSet | null> {
  const { data: rows } = await listCategoryQuestions(categoryId);
  if (!rows || rows.length === 0) return null;

  const makeOptions = (
    opt1: string, opt2: string, opt3: string, opt4: string, opt5: string,
    mappings: Omit<AltitudeQuestionOption, 'label' | 'description'>[]
  ): AltitudeQuestionOption[] => [
    { ...mappings[0]!, label: opt1, description: opt1 },
    { ...mappings[1]!, label: opt2, description: opt2 },
    { ...mappings[2]!, label: opt3, description: opt3 },
    { ...mappings[3]!, label: opt4, description: opt4 },
    { ...mappings[4]!, label: opt5, description: opt5 },
  ];

  const questions: AltitudeQuestion[] = rows.map((row) => {
    const dim = row.dimension;
    const baseMappings =
      dim === 'timeHorizon' ? TIME_HORIZON_BASE_OPTIONS :
      dim === 'workSize'    ? WORK_SIZE_BASE_OPTIONS :
                              IMPACT_SURFACE_BASE_OPTIONS;
    return {
      id: `${categorySlug}_${dim}`,
      dimension: dim,
      question: row.questionText,
      options: makeOptions(row.option1, row.option2, row.option3, row.option4, row.option5, baseMappings),
    };
  });

  return {
    candidateType: categorySlug as ItemLanguageType,
    displayName: categoryLabel,
    description: categoryDescription,
    questions,
  };
}


/**
 * Convert question responses to AltitudeScopingInputs
 */
export function responsesToScopingInputs(
  responses: AltitudeQuestionResponse[]
): AltitudeScopingInputs {
  const inputs: AltitudeScopingInputs = {};
  
  for (const response of responses) {
    if (response.dimension === 'timeHorizon') {
      inputs.timeHorizon = response.mappedValue as TimeHorizon;
    } else if (response.dimension === 'workSize') {
      inputs.workSize = response.mappedValue as WorkSize;
    } else if (response.dimension === 'impactSurface') {
      inputs.impactSurface = response.mappedValue as ImpactSurface;
    }
  }
  
  return inputs;
}



/**
 * Get a quick altitude estimate from a single "reversibility" question
 * This is a shortcut heuristic for when full questions aren't needed
 */
export function getReversibilityBasedAltitude(reversibilityScore: 1 | 2 | 3 | 4 | 5): AltitudeLevel {
  // Reversibility directly maps to altitude:
  // 1 = easily reversible = tactical (altitude 1)
  // 5 = hard to reverse = strategic (altitude 5)
  return reversibilityScore;
}
